import { useMemo } from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Link,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { PairMapperItem } from "../api/usePairMapper";

interface Props {
  pairs: PairMapperItem[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
}

const matchColor = (matchedBy: string) => {
  switch (matchedBy) {
    case "manual":
      return "success";
    case "ai_confirmed":
      return "info";
    case "ai_pending":
    case "keyword_pending":
      return "warning";
    default:
      return "default";
  }
};

const normalizeMatchedBy = (matchedBy: string) =>
  matchedBy === "keyword_confirmed" ? "manual" : matchedBy;

const statusColor = (status: string) => {
  switch (status) {
    case "active":
      return "success";
    case "paused":
      return "warning";
    case "expired":
      return "error";
    default:
      return "default";
  }
};

export const PairTable = ({
  pairs,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  onConfirm,
  onReject,
}: Props) => {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        minWidth: 250,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value} placement="top-start">
            <Typography variant="body2" noWrap sx={{ fontSize: "0.8rem" }}>
              {params.value}
            </Typography>
          </Tooltip>
        ),
      },
      {
        headerName: "Polymarket",
        field: "siteA",
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const site = params.value;
          return site?.marketUrl ? (
            <Link
              href={site.marketUrl}
              target="_blank"
              rel="noopener"
              sx={{ fontSize: "0.75rem" }}
            >
              {site.marketId.slice(0, 10)}...
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {site?.marketId?.slice(0, 10) || "-"}
            </Typography>
          );
        },
      },
      {
        headerName: "Kalshi",
        field: "siteB",
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const site = params.value;
          return site?.marketUrl ? (
            <Link
              href={site.marketUrl}
              target="_blank"
              rel="noopener"
              sx={{ fontSize: "0.75rem" }}
            >
              {site.marketId}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {site?.marketId || "-"}
            </Typography>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.value}
            size="small"
            color={statusColor(params.value) as any}
            sx={{ fontSize: "0.65rem" }}
          />
        ),
      },
      {
        headerName: "Match",
        field: "matchedBy",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as PairMapperItem;
          const normalizedMatchedBy = normalizeMatchedBy(params.value);
          const tooltipText = row.keywordOverlap?.length
            ? `Keywords: ${row.keywordOverlap.join(", ")}`
            : normalizedMatchedBy;

          return (
            <Tooltip title={tooltipText}>
              <Chip
                label={normalizedMatchedBy}
                size="small"
                color={matchColor(normalizedMatchedBy) as any}
                sx={{ fontSize: "0.6rem" }}
              />
            </Tooltip>
          );
        },
      },
      {
        headerName: "Conf.",
        field: "matchConfidence",
        width: 65,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) =>
          params.value != null
            ? `${(params.value * 100).toFixed(0)}%`
            : "-",
      },
      {
        headerName: "Keywords",
        field: "keywordOverlap",
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const keywords = params.value as string[] | undefined;
          if (!keywords?.length) return "-";
          return (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {keywords.slice(0, 3).map((kw: string) => (
                <Chip
                  key={kw}
                  label={kw}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.6rem", height: 20 }}
                />
              ))}
              {keywords.length > 3 && (
                <Tooltip title={keywords.join(", ")}>
                  <Chip
                    label={`+${keywords.length - 3}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.6rem", height: 20 }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as PairMapperItem;
          const normalizedMatchedBy = normalizeMatchedBy(row.matchedBy);
          const isPending =
            normalizedMatchedBy === "ai_pending" ||
            normalizedMatchedBy === "keyword_pending";

          return (
            <Box display="flex" gap={0.5}>
              {isPending && (
                <Tooltip title="Confirm match">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onConfirm(row._id)}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Reject / Unmatch">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onReject(row._id)}
                >
                  <HighlightOffIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [onConfirm, onReject],
  );

  return (
    <DataGrid
      rows={pairs}
      columns={columns}
      getRowId={(row) => row._id}
      loading={isLoading}
      autoHeight
      disableSelectionOnClick
      paginationMode="server"
      rowCount={total}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      rowsPerPageOptions={[25, 50, 100]}
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        },
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "rgba(255,255,255,0.03)",
        },
      }}
    />
  );
};
